/**
 * @license
 * MIT License
 *
 * Copyright (c) 2021 Floating UI contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useLayoutEffect, useCallback, useState, useMemo, useRef, MutableRef } from 'preact/hooks';
import { useUser } from '../UNSAFE_useUser';
import { normalizePosition, reverseNormalizePosition } from '../../utils/PRIVATE_floatingUtils';
import {
  computePosition,
  offset as floatingOffset,
  MiddlewareData,
  Middleware,
  Placement as FloatingPlacement,
  MiddlewareArguments,
  ComputePositionConfig
} from '@floating-ui/dom';
import {
  Offset,
  Placement,
  Rect,
  PositionData,
  RtlSide
} from '../../UNSAFE_Floating/Floating.types';

export {
  autoPlacement,
  flip,
  hide,
  shift,
  limitShift,
  size,
  inline,
  detectOverflow
} from '@floating-ui/dom';

type CornerPlacement = {
  placement: Placement;
  offset?: { mainAxis?: number; crossAxis?: number };
};

type Side = 'top' | 'right' | 'bottom' | 'left';
type Strategy = 'absolute' | 'fixed';
type SideObject = {
  [key in Side]: number;
};
type FloatingOffsetValue =
  | number
  | {
      /**
       * The axis that runs along the side of the floating element.
       * @default 0
       */
      mainAxis?: number;
      /**
       * The axis that runs along the alignment of the floating element.
       * @default 0
       */
      crossAxis?: number;
      /**
       * When set to a number, overrides the `crossAxis` value for aligned
       * (non-centered/base) placements and works logically. A positive number
       * will move the floating element in the direction of the opposite edge
       * to the one that is aligned, while a negative number the reverse.
       * @default null
       */
      alignmentAxis?: number | null;
    };

export const offset = (value?: Offset): Middleware => {
  return {
    name: 'offset',
    options: value,

    async fn(middlewareArguments: MiddlewareArguments) {
      const newFn = (args: MiddlewareArguments) => {
        // flip middleware could be called several times, until the right space is found.
        // Every time flip is called and overflow is detected, a possible next placement
        // is chosen and the array of middleware (including the offset middleware) is called again
        // with the chosen next placement.
        // Here we detect that flip is needed and we should change the offset sign if needed.
        const overflowsData =
          args.middlewareData.flip?.overflows != undefined
            ? args.middlewareData.flip?.overflows[0]
            : null;
        let offset;
        if (overflowsData != null && value != null && typeof value === 'object') {
          offset = flippedOffset(args.placement, args.initialPlacement, value);
        }
        if (!value) {
          return 0;
        }
        const rawValue: FloatingOffsetValue = offset ?? value;
        return rawValue;
      };

      return floatingOffset(newFn).fn(middlewareArguments);
    }
  };
};

const flippedOffset = (
  placement: FloatingPlacement,
  initialPlacement: FloatingPlacement,
  offset: Offset
): Offset => {
  const initialSide = initialPlacement.split('-')[0] as RtlSide;
  const side = placement.split('-')[0] as RtlSide;
  const initialAlignment = initialPlacement.split('-')[1];
  const alignment = placement.split('-')[1];
  // if flipped around crossAxis
  if (
    ((initialSide === 'right' || initialSide === 'left') &&
      initialSide === side &&
      initialAlignment !== alignment) ||
    ((initialSide === 'top' || initialSide === 'bottom') &&
      initialSide === side &&
      initialAlignment !== alignment)
  ) {
    return {
      mainAxis: offset && typeof offset === 'object' && offset.mainAxis ? offset.mainAxis : 0,
      crossAxis: offset && typeof offset === 'object' && offset.crossAxis ? -offset.crossAxis : 0
    };
  } else {
    return offset;
  }
};

type ClientRectObject = Rect & SideObject;
export type VirtualElement = {
  getBoundingClientRect(): ClientRectObject;
  contextElement?: any;
};

type ReferenceType = Element | VirtualElement;
type UseFloatingProps<RT extends ReferenceType = ReferenceType> = Omit<
  Partial<ComputePositionConfig>,
  'platform' | 'placement'
> & {
  placement: Placement;
  whileElementsMounted?: (
    reference: RT,
    floating: HTMLElement,
    update: () => void
  ) => void | (() => void);
  onPosition?: (data: PositionData) => void;
};

type UseFloatingData = {
  placement: FloatingPlacement;
  strategy: Strategy;
  middlewareData: MiddlewareData;
  x: number | null;
  y: number | null;
};

type UseFloatingReturn<RT extends ReferenceType = ReferenceType> = UseFloatingData & {
  update: () => void;
  reference: (node: RT | null) => void;
  floating: (node: HTMLElement | null) => void;
  refs: {
    reference: MutableRef<RT | null>;
    floating: MutableRef<HTMLElement | null>;
  };
};

// Fork of `fast-deep-equal` that only does the comparisons we need and compares
// functions
function deepEqual(a: any, b: any) {
  if (a === b) {
    return true;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (typeof a === 'function' && a.toString() === b.toString()) {
    return true;
  }

  let length, i, keys;

  if (a && b && typeof a == 'object') {
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0; ) {
        if (!deepEqual(a[i], b[i])) {
          return false;
        }
      }

      return true;
    }

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) {
      return false;
    }

    for (i = length; i-- !== 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }

    for (i = length; i-- !== 0; ) {
      const key = keys[i];
      if (key === '_owner' && a.$$typeof) {
        continue;
      }

      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }

    return true;
  }

  return a !== a && b !== b;
}

function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

const reverseLogicalSide = (placement: FloatingPlacement, direction: 'rtl' | 'ltr'): Placement => {
  const side = placement.split('-')[0] as RtlSide;
  if (side === 'top' || side === 'bottom') {
    return placement as Placement;
  }
  const alignment = placement.split('-')[1];
  const reversedPhysicalSide = reverseNormalizePosition(side, direction);
  const alignmentSideHash = {
    start: 'top',
    end: 'bottom'
  };
  const alignmentMappedSide =
    alignment != null
      ? alignment.replace(/start|end/g, (matched) => (alignmentSideHash as any)[matched])
      : null;

  const placements: Array<Placement> = [
    'top',
    'top-start',
    'top-end',
    'end',
    'end-top',
    'end-bottom',
    'bottom-start',
    'bottom-end',
    'bottom',
    'start',
    'start-top',
    'start-bottom'
  ];

  const newPlacement: Placement = placements.filter(
    (placement) =>
      (alignmentMappedSide && placement === `${reversedPhysicalSide}-${alignmentMappedSide}`) ||
      (!alignmentMappedSide && placement === `${reversedPhysicalSide}`)
  )[0];
  if (!newPlacement) {
    return 'bottom';
  }

  return newPlacement;
};

export const logicalSide = (placement: Placement, direction: 'rtl' | 'ltr'): FloatingPlacement => {
  const side = placement.split('-')[0] as RtlSide;
  const alignment = placement.split('-')[1];
  const physicalSide = normalizePosition(side, direction);

  let alignmentSideHash: any;
  let alignmentMappedSide: string;
  if (alignment) {
    alignmentSideHash = {
      top: 'start',
      bottom: 'end'
    };

    alignmentMappedSide = alignment.replace(
      /top|bottom/g,
      (matched) => (alignmentSideHash as any)[matched]
    );
  }

  const placements: Array<FloatingPlacement> = [
    'top',
    'right',
    'bottom',
    'left',
    'top-start',
    'right-start',
    'bottom-start',
    'left-start',
    'top-end',
    'right-end',
    'bottom-end',
    'left-end'
  ];
  const newPlacement: FloatingPlacement = placements.filter(
    (placement) =>
      (alignmentMappedSide && placement === `${physicalSide}-${alignmentMappedSide}`) ||
      (!alignmentMappedSide && placement === `${physicalSide}`)
  )[0];
  if (!newPlacement) {
    return 'bottom';
  }

  return newPlacement;
};

const cornerPlacement = (placement: Placement, floating: HTMLElement): CornerPlacement => {
  const side = placement.split('-')[0];
  const alignment = placement.split('-')[1];
  const corner = placement.split('-')[2];
  if (corner == null) {
    return { placement: placement, offset: undefined };
  }

  const placements: Array<Placement> = [
    'top-start',
    'start-top',
    'bottom-start',
    'start-bottom',
    'top-end',
    'end-top',
    'bottom-end',
    'end-bottom'
  ];
  const newPlacement: Placement = placements.filter(
    (placement) => placement === `${side}-${alignment}`
  )[0];
  if (alignment == 'start') {
    return { placement: newPlacement, offset: { crossAxis: -floating.offsetWidth } };
  }
  if (alignment == 'end') {
    return { placement: newPlacement, offset: { crossAxis: floating.offsetWidth } };
  }
  if (alignment == 'top') {
    return { placement: newPlacement, offset: { crossAxis: -floating.offsetHeight } };
  }
  // the last option should always be alignment == 'bottom'
  return { placement: newPlacement, offset: { crossAxis: floating.offsetHeight } };
};

const isOfPlacementType = (value: string): boolean => {
  const placementTypeValues = [
    'top',
    'top-start',
    'top-end',
    'top-start-corner',
    'top-end-corner',
    'start',
    'start-top',
    'start-bottom',
    'start-top-corner',
    'start-bottom-corner',
    'bottom',
    'bottom-start',
    'bottom-end',
    'bottom-start-corner',
    'bottom-end-corner',
    'end',
    'end-top',
    'end-bottom',
    'end-top-corner',
    'end-bottom-corner'
  ];

  return placementTypeValues.includes(value);
};

export function useFloating<RT extends ReferenceType = ReferenceType>({
  middleware,
  placement,
  strategy,
  onPosition,
  whileElementsMounted
}: UseFloatingProps): UseFloatingReturn<RT> {
  //this should not happen, checking to just to be safe
  if (!isOfPlacementType(placement)) {
    placement = 'bottom';
  }
  const referenceRef = useRef<RT | null>(null);
  const floatingRef = useRef<HTMLElement | null>(null);

  const [reference, _setReference] = useState<RT | null>(null);
  const [floating, _setFloating] = useState<HTMLElement | null>(null);

  const whileElementsMountedRef = useLatestRef(whileElementsMounted);
  const isMountedRef = useRef(false);
  const { direction } = useUser();

  const [data, _setData] = useState<UseFloatingData>({
    // Setting these to `null` will allow the consumer to determine if
    // `computePosition()` has run yet
    x: null,
    y: null,
    strategy: strategy ?? 'absolute',
    placement: 'bottom',
    middlewareData: {}
  });
  const dataRef = useRef(data);
  const setData = (data: UseFloatingData) => {
    dataRef.current = data;
    _setData(data);
  };

  const [latestMiddleware, setLatestMiddleware] = useState(middleware);

  if (
    !deepEqual(
      latestMiddleware?.map(({ options }) => options),
      middleware?.map(({ options }) => options)
    )
  ) {
    setLatestMiddleware(middleware);
  }

  const update = useCallback(() => {
    if (!referenceRef.current || !floatingRef.current) {
      return;
    }

    // map *-corner placement to normalized placement and offset
    const floatingCornerPlacement: CornerPlacement = cornerPlacement(
      placement,
      floatingRef.current
    );
    // map start/end placements to right/left depending on the reading direction
    const floatingUiPlacement: FloatingPlacement = logicalSide(
      floatingCornerPlacement.placement,
      direction
    );

    // we need a summarized offset middleware
    const newMiddlewareArray: Middleware[] | undefined = latestMiddleware?.map((m) => {
      if (m.name === 'offset') {
        if (typeof m.options === 'function') {
          // ignore function if there are *-corner placements
          return floatingCornerPlacement.offset == null
            ? m
            : offset(floatingCornerPlacement.offset);
        }

        const currentOffset =
          typeof m.options === 'number' ? { mainAxis: m.options, crossAxis: 0 } : m.options;
        return offset({
          mainAxis: (currentOffset && currentOffset.mainAxis) ?? 0,
          crossAxis:
            ((floatingCornerPlacement.offset && floatingCornerPlacement.offset.crossAxis) ?? 0) +
            ((currentOffset && currentOffset.crossAxis) ?? 0)
        });
      } else {
        return m;
      }
    });
    computePosition(referenceRef.current, floatingRef.current, {
      middleware: newMiddlewareArray,
      placement: floatingUiPlacement,
      strategy
    }).then((newData) => {
      if (isMountedRef.current) {
        if (
          newData.placement !== dataRef.current.placement ||
          newData.x !== dataRef.current.x ||
          newData.y !== dataRef.current.y ||
          newData.middlewareData.arrow?.x != dataRef.current.middlewareData.arrow?.x ||
          newData.middlewareData.arrow?.y != dataRef.current.middlewareData.arrow?.y ||
          newData.middlewareData.arrow?.centerOffset !=
            dataRef.current.middlewareData.arrow?.centerOffset
        ) {
          const origOffset = newMiddlewareArray?.find((m) => m.name === 'offset')?.options;
          const reversedNewPlacement = reverseLogicalSide(newData.placement, direction);
          const offset = newData.middlewareData.offset;
          const mappedData = {
            // we need to map right/left, returned from the computePosition, to start/end
            placement: reversedNewPlacement,
            origPlacement: placement,
            x: newData.x,
            y: newData.y,
            arrow: newData.middlewareData.arrow,
            offset: offset,
            origOffset: origOffset
          };
          onPosition?.(mappedData);
        }
        setData(newData);
      }
    });
  }, [latestMiddleware, placement, direction, strategy, onPosition]);

  useLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (reference && floating) {
      if (whileElementsMountedRef.current) {
        return whileElementsMountedRef.current(reference, floating, update);
      } else {
        update();
      }
    }
  }, [reference, floating, update, whileElementsMountedRef]);

  const setReference = useCallback((node: RT | null) => {
    if (referenceRef.current !== node) {
      referenceRef.current = node;
      _setReference(node);
    }
  }, []);

  const setFloating = useCallback((node: HTMLElement | null) => {
    if (floatingRef.current !== node) {
      floatingRef.current = node;
      _setFloating(node);
    }
  }, []);

  const refs = useMemo(() => ({ reference: referenceRef, floating: floatingRef }), []);

  return useMemo(
    () => ({
      ...data,
      update,
      refs,
      reference: setReference,
      floating: setFloating
    }),
    [data, update, refs, setReference, setFloating]
  );
}
